// The in-app Claude sign-in: the agent-service's /agent/llm-login routes (omega-ai-agent-service,
// src/llmLogin/routes.ts). The person signs in on Claude's own page and pastes the code Claude shows; the
// bridge trades that code with Anthropic, stores the token that comes back for the signed-in Account, and
// answers with the redacted summary. No token ever reaches this page.
import { AGENT_HTTP } from "./agentUrl";

export class LoginFlowError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
  }
}

export interface ClaudeLoginStart {
  loginId: string;
  url: string;
  expiresInMs: number;
}

export interface ClaudeLoginStored {
  state: "stored";
  login: { present: boolean; kind?: string; "set-at"?: number; "expires-at"?: number };
}

async function call<T>(action: "start" | "code" | "cancel", token: string, body: unknown): Promise<T> {
  const res = await fetch(`${AGENT_HTTP}/agent/llm-login/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-omega-credential": token },
    body: JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new LoginFlowError(String(data.code ?? `HTTP_${res.status}`), String(data.error ?? `The sign-in service answered ${res.status}.`));
  }
  return data as T;
}

// The bridge hands back Claude's own authorize URL, but a URL is data, not a guarantee. Navigate
// only to https on claude.com or one of its subdomains; anything else is refused and shown.
export const isClaudeSignInUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:") return false;
    const host = url.hostname.toLowerCase();
    return host === "claude.com" || host.endsWith(".claude.com");
  } catch {
    return false;
  }
};

export const startClaudeLogin = (token: string) => call<ClaudeLoginStart>("start", token, {});

export const finishClaudeLogin = (token: string, loginId: string, code: string) =>
  call<ClaudeLoginStored>("code", token, { loginId, code });

export const cancelClaudeLogin = (token: string) => call<void>("cancel", token, {});
